import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, ExternalLink, RotateCcw } from 'lucide-react';
import { Button } from '../../components/shared';
import { VendorPageHeader } from '../../components/vendor/guidance';
import useOperatingMarkets from '../../hooks/useOperatingMarkets';
import {
 useUpdateVendorTaxProfile,
 useVendorTaxProfile,
 useVendorStripeConnect,
 vendorStripeConnectApi,
} from '../../services/vendorTaxProfileService';
import { useToastContext } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import StripeConnectStatusBadge from '../../components/shared/StripeConnectStatusBadge';
import { getStripeConnectStatus } from '../../utils/stripeConnectStatus';
import { marketCountryCode, toMarketSelectOptions } from '../../utils/marketOptions';

const VendorTaxProfile = () => {
 const { user } = useAuth();
 const { success, error: showError } = useToastContext();
 const queryClient = useQueryClient();
 const [searchParams, setSearchParams] = useSearchParams();
 const { data, isLoading } = useVendorTaxProfile();
 const { data: connectRes, isLoading: connectLoading } = useVendorStripeConnect();
 const connect = connectRes?.data;
 const connectStatus = getStripeConnectStatus(connect);
 const { markets, isLoading: marketsLoading } = useOperatingMarkets({
 activeOnly: true,
 });
 const [connectBusy, setConnectBusy] = useState(false);
 const handledConnectFlag = useRef(null);
 const syncedPayoutRef = useRef(false);

 const saveMutation = useUpdateVendorTaxProfile({
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey: ['vendor-stripe-connect'] });
 queryClient.invalidateQueries({ queryKey: ['vendor-tax-profile'] });
 },
 onError: (err) => {
 showError(err?.response?.data?.message || err?.message || 'Could not sync payout market');
 },
 });

 const vendorMarketCode = useMemo(() => {
 const fromProfile = String(user?.vendor_profile?.country || '').trim().toUpperCase();
 const fromTax = String(
 data?.data?.payout_country || data?.data?.tax_residence_country || ''
 ).trim().toUpperCase();
 return fromProfile || fromTax || '';
 }, [user?.vendor_profile?.country, data?.data?.payout_country, data?.data?.tax_residence_country]);

 const selectedMarketLabel = useMemo(() => {
 if (!vendorMarketCode) return null;
 const match = (markets || []).find(
 (m) => marketCountryCode(m) === vendorMarketCode
 );
 if (match) {
 return toMarketSelectOptions([match])[0]?.label || vendorMarketCode;
 }
 return vendorMarketCode;
 }, [markets, vendorMarketCode]);

 // Keep tax/payout country aligned with the vendor operating market (set at verification)
 useEffect(() => {
 if (syncedPayoutRef.current || isLoading || !vendorMarketCode || !data?.data) return;
 const row = data.data;
 const current = String(row.payout_country || row.tax_residence_country || '')
 .trim()
 .toUpperCase();
 if (current === vendorMarketCode) {
 syncedPayoutRef.current = true;
 return;
 }
 syncedPayoutRef.current = true;
 saveMutation.mutate({
 payout_country: vendorMarketCode,
 tax_residence_country: vendorMarketCode,
 mark_complete: false,
 });
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [isLoading, vendorMarketCode, data?.data?.payout_country, data?.data?.tax_residence_country]);

 useEffect(() => {
 const flag = searchParams.get('connect');
 if (!flag) {
 handledConnectFlag.current = null;
 return;
 }
 if (handledConnectFlag.current === flag) return;
 handledConnectFlag.current = flag;
 const next = new URLSearchParams(searchParams);
 next.delete('connect');
 setSearchParams(next, { replace: true });
 queryClient.invalidateQueries({ queryKey: ['vendor-stripe-connect'] });
 queryClient.invalidateQueries({ queryKey: ['vendor-tax-profile'] });
 if (flag === 'return' || flag === 'refresh') {
 success('Stripe Connect onboarding updated — status refreshed.');
 }
 }, [searchParams, setSearchParams, queryClient, success]);

 const startConnect = async () => {
 setConnectBusy(true);
 try {
 const res = await vendorStripeConnectApi.onboard();
 const url = res?.data?.url;
 if (!url) throw new Error('No onboarding URL returned');
 window.location.href = url;
 } catch (err) {
 showError(err?.response?.data?.message || err?.message || 'Could not start Connect onboarding');
 setConnectBusy(false);
 }
 };

 const resetConnect = async () => {
 if (
 !window.confirm(
 'Remove this Stripe Connect account and start over? In test mode the Express account is deleted from Stripe.'
 )
 ) {
 return;
 }
 setConnectBusy(true);
 try {
 await vendorStripeConnectApi.disconnect({ delete_remote: true });
 await queryClient.invalidateQueries({ queryKey: ['vendor-stripe-connect'] });
 success('Stripe Connect reset. You can start onboarding again.');
 } catch (err) {
 showError(err?.response?.data?.message || err?.message || 'Could not reset Connect');
 } finally {
 setConnectBusy(false);
 }
 };

 const openConnectDashboard = async () => {
 setConnectBusy(true);
 try {
 const res = await vendorStripeConnectApi.dashboardLink();
 const url = res?.data?.url;
 if (!url) throw new Error('No dashboard URL returned');
 window.open(url, '_blank', 'noopener,noreferrer');
 } catch (err) {
 showError(err?.response?.data?.message || err?.message || 'Could not open Stripe dashboard');
 } finally {
 setConnectBusy(false);
 }
 };

 if (isLoading) {
 return (
 <div className="flex justify-center py-24">
 <Loader2 className="w-8 h-8 animate-spin text-ink" />
 </div>
 );
 }

 return (
 <div className="space-y-5 max-w-3xl">
 <VendorPageHeader
 title="Payouts"
 subtitle="Connect Stripe so released earnings can reach your bank. Stripe collects your tax and identity details during onboarding."
 />

 <section className="product-panel p-5 sm:p-6 space-y-4">
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div>
 <h2 className="text-base font-semibold text-ink">Stripe Connect</h2>
 <p className="text-sm leading-6 text-ink-secondary mt-1">
 Start Connect and finish the Stripe Express form. That is where you enter tax ID, legal name,
 and bank details. You do not fill a separate tax profile here.
 Stripe does not open merchant accounts in most of Africa, including Ghana and Nigeria —
 if Connect is unavailable, an admin can still pay you by bank or mobile money.
 </p>
 </div>
 {!connectLoading ? <StripeConnectStatusBadge connect={connect} /> : null}
 </div>
 {connectLoading ? (
 <Loader2 className="w-5 h-5 animate-spin text-ink" />
 ) : (
 <>
 <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
 <div>
 <dt className="text-xs uppercase tracking-wide text-gray-500">Status</dt>
 <dd className="mt-1">
 <span
 className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${connectStatus.className}`}
 >
 {connectStatus.label}
 </span>
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wide text-gray-500">Payout ready</dt>
 <dd className="font-medium text-ink">
 {connect?.is_payout_ready ? 'Yes' : 'No'}
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wide text-gray-500">Account</dt>
 <dd className="font-mono text-xs text-ink-secondary">
 {connect?.account_id || '—'}
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wide text-gray-500">Connect country</dt>
 <dd className="font-medium text-ink">
 {connect?.resolution?.connect_country || '—'}
 {connect?.resolution?.source ? (
 <span className="block text-xs font-normal text-gray-500 mt-0.5">
 via {connect.resolution.source.replace(/_/g, ' ')}
 {connect.resolution.connect_currency
 ? ` · ${connect.resolution.connect_currency}`
 : ''}
 </span>
 ) : null}
 </dd>
 </div>
 </dl>
 {connect?.is_test_mode ? (
 <p className="text-sm text-ink bg-surface-muted rounded-xl px-3 py-2">
 {connect.test_onboarding_hint ||
 'Test mode is on — use Stripe test identity and bank details in Express onboarding.'}
 </p>
 ) : null}
 {connect?.resolution_error ? (
 <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
 {connect.resolution_error}
 </p>
 ) : null}
 <div className="flex flex-wrap gap-2">
 <Button
 type="button"
 className="rounded-md"
 disabled={connectBusy || connect?.can_start_onboarding === false}
 onClick={startConnect}
 >
 {connectBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
 {connect?.account_id ? 'Continue / update onboarding' : 'Start Stripe Connect'}
 </Button>
 {connect?.details_submitted && (
 <Button
 type="button"
 variant="secondary"
 className="rounded-md"
 disabled={connectBusy}
 onClick={openConnectDashboard}
 >
 <ExternalLink className="w-4 h-4 mr-2" />
 Stripe Express dashboard
 </Button>
 )}
 {connect?.can_reset ? (
 <Button
 type="button"
 variant="secondary"
 className="rounded-md"
 disabled={connectBusy}
 onClick={resetConnect}
 >
 <RotateCcw className="w-4 h-4 mr-2" />
 Remove & start over
 </Button>
 ) : null}
 </div>
 </>
 )}
 </section>

 <section className="product-panel p-5 sm:p-6 space-y-3">
 <div>
 <h2 className="text-sm font-semibold text-ink">Payout market</h2>
 <p className="mt-1 text-sm text-ink-secondary">
 Taken from the operating market you chose at verification. Stripe Connect uses this country.
 </p>
 </div>
 <div className="rounded-xl border border-border bg-surface-muted px-4 py-3">
 {marketsLoading ? (
 <div className="flex items-center gap-2 text-sm text-gray-500">
 <Loader2 className="w-4 h-4 animate-spin" />
 Loading market…
 </div>
 ) : selectedMarketLabel ? (
 <p className="text-sm font-medium text-ink">
 {selectedMarketLabel}
 </p>
 ) : (
 <p className="text-sm text-amber-700">
 No operating market on your profile yet. Complete verification or update your business profile.
 </p>
 )}
 </div>
 {saveMutation.isPending ? (
 <p className="text-xs text-gray-500 inline-flex items-center gap-1.5">
 <Loader2 className="w-3.5 h-3.5 animate-spin" />
 Syncing with payout settings…
 </p>
 ) : null}
 </section>
 </div>
 );
};

export default VendorTaxProfile;
